import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { DownloadIcon, FileSignatureIcon } from 'lucide-react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import type { PortalValidationProof } from '@/api/types'
import { portalValidationApi } from '@/api/validation-proofs'
import { EmptyState } from '@/components/empty-state'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { PORTAL_PROOFS_KEY } from '@/components/portal/signed-gesture'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { downloadDocument } from '@/hooks/use-document-content'
import { msg } from '@/i18n'
import { formatDateTime } from '@/lib/format'
import { matching, readSearch } from '@/lib/search-params'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/portal/validations')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch(
      {
        /** La preuve à mettre en évidence : la cloche et le reçu y mènent. */
        preuve: matching(/^[0-9a-f]{24}$/),
      },
      search,
    ),
  component: PortalValidationsPage,
})

/**
 * « Mes validations signées » (lot 31) : les preuves du client, les plus
 * récentes d'abord — ce qui a été validé, par qui, quand, l'empreinte, et le
 * PDF figé à télécharger. Tout contact du client les lit.
 */
function PortalValidationsPage() {
  const { preuve } = Route.useSearch()
  const t = msg().portalRecipe.validations
  const proofs = useQuery({
    queryKey: [...PORTAL_PROOFS_KEY, null],
    queryFn: () => portalValidationApi.proofs(),
  })

  return (
    <div className="space-y-5">
      <PageHeader title={t.title} />
      <Frame>
        {proofs.isPending ? (
          <Skeleton className="h-32 w-full rounded-md" />
        ) : proofs.isError ? (
          <FormError>
            {proofs.error instanceof ApiError ? proofs.error.message : t.failed}
          </FormError>
        ) : proofs.data.length === 0 ? (
          <EmptyState
            icon={FileSignatureIcon}
            title={t.emptyTitle}
            description={t.emptyDescription}
            action={
              <Button asChild variant="outline">
                <Link to="/portal/projets">{t.seeProjects}</Link>
              </Button>
            }
          />
        ) : (
          <ul className="divide-y" data-testid="preuves">
            {proofs.data.map((proof) => (
              <ProofRow key={proof.id} proof={proof} highlighted={proof.id === preuve} />
            ))}
          </ul>
        )}
      </Frame>
    </div>
  )
}

function ProofRow({ proof, highlighted }: { proof: PortalValidationProof; highlighted: boolean }) {
  const t = msg().portalRecipe.validations
  return (
    <li
      className={cn(
        'flex flex-wrap items-start gap-3 py-3',
        highlighted && '-mx-2 rounded-md bg-primary/5 px-2',
      )}
      data-testid="preuve"
      data-surligne={highlighted ? 'oui' : undefined}
    >
      <FileSignatureIcon className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1 text-sm">
        <p className="font-medium">{proof.object_label}</p>
        <p className="text-muted-foreground">
          {t.signedBy(proof.signed_name, formatDateTime(proof.signed_at))}
          {proof.project ? ` · ${proof.project.name}` : ''} · {t.proofNumber}{' '}
          <span className="font-key">{proof.short_id}</span>
        </p>
        <p className="text-xs text-muted-foreground italic">« {proof.statement} »</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          void downloadDocument(
            portalValidationApi.pdfPath(proof.id),
            `preuve-${proof.short_id}.pdf`,
          ).catch(() => {
            toast.error(t.downloadFailed)
          })
        }}
      >
        <DownloadIcon />
        {t.pdf}
      </Button>
    </li>
  )
}
